import { CheckboxGroup } from '@designsforhealth/dfh-react-components'
import { ExpandLess, ExpandMore } from '@material-ui/icons'
import { createKeyedSignal } from '@react-rxjs/utils'
import * as _ from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { distinctUntilChanged, map, Observable, skipWhile } from 'rxjs'
import { filter } from 'rxjs/operators'
import styled from 'styled-components'

import {
  parseFilterOptionsForCheckboxGroup,
  ProductAggregation,
  ProductAggregations,
  ProductAggregationsKey,
  UseProducts,
} from '../../../../lib/products'

export interface FilterProps {
  className?: string
  id: ProductAggregationsKey
  aggregation: ProductAggregation
  listing: UseProducts['listing']
}

type FormFields = Record<keyof ProductAggregations, string[]>

const sortValues = (values?: ReadonlyArray<string> | null): string[] => {
  return _.uniq([...(values || [])].sort())
}

const sortOptionsByLabel = (
  options: ReadonlyArray<ProductAggregation['options'][number]>,
): ProductAggregation['options'] =>
  [...options].sort((a, b) =>
    (a.label ?? '').localeCompare(b.label ?? '', 'en', {
      numeric: true,
    }),
  )

const mapLabelStatusOptions = (
  options: ReadonlyArray<ProductAggregation['options'][number]>,
): ProductAggregation['options'] =>
  options.map((option) =>
    option.label === 'Present'
      ? { ...option, label: 'On File' }
      : option.label === 'Absent'
        ? { ...option, label: 'Not On File' }
        : option,
  )

const defaultValuesForState = (
  id: ProductAggregationsKey,
  options: ProductAggregation['options'],
  stateValues?: string[] | null,
) =>
  // if only one checkbox, react-hook-form considers an empty array as checked value
  // if more than one, then provide array of values or empty array
  stateValues && stateValues.length > 0
    ? {
        [id]: options.length === 1 ? stateValues[0] : stateValues,
      }
    : {
        [id]: options.length === 1 ? null : [],
      }

/**
 * "Raw" form input
 */
interface RawKeyedInput {
  id: ProductAggregationsKey
  input: boolean | string | ReadonlyArray<string> | null
}

/**
 * Normalized form input
 */
interface KeyedInput {
  id: ProductAggregationsKey
  input: ReadonlyArray<string>
}

const [formInputById$, setFormInput] = createKeyedSignal(
  ({ id }) => id,
  ({ id, input }: RawKeyedInput): KeyedInput => {
    // normalize form values
    return typeof input === 'string'
      ? { id, input: [input] }
      : typeof input === 'boolean' || input == null
        ? { id, input: [] }
        : { id, input }
  },
)

const rejectInvalidValues =
  (allowedValues: string[]) =>
  (values: ReadonlyArray<string>): { hasRejected: boolean; values: string[] } => {
    return values.reduce(
      (acc: { hasRejected: boolean; values: string[] }, value) => {
        if (allowedValues.indexOf(value) >= 0) {
          acc.values.push(value)
        } else {
          acc.hasRejected = true
        }
        return acc
      },
      { hasRejected: false, values: [] },
    )
  }

/**
 * Build observable for given product listing that emits a function to apply new filters to listing
 * when form input changes.
 */
const applyFiltersOnFormInput$ = (
  id: ProductAggregationsKey,
  options: ProductAggregation['options'],
  listing: UseProducts['listing'],
  formInputToSkip: ReadonlyArray<string>,
): Observable<{ newFilters: string[]; applyFilters: () => void }> => {
  const { state, apply, nextState } = listing
  const allowedValues = options.map((option) => option.value)
  const stateFilters = sortValues(state.filters[id])

  return formInputById$(id).pipe(
    // extract input from keyed signal
    map(({ input }) => input),
    distinctUntilChanged(
      // help TS with type inference
      (a, b) => _.isEqual(a, b),
    ),
    // skip initial form input values to avoid accidental application of previous values
    skipWhile((inputValues) => _.isEqual(inputValues, formInputToSkip)),
    map(sortValues),
    map(rejectInvalidValues(allowedValues)),
    filter(({ values: formFilters }) => !_.isEqual(stateFilters, formFilters)),
    map(({ hasRejected, values: newFilters }) => {
      const next = nextState.withFilters({ [id]: newFilters })
      // if input values were rejected, then replace current browser history entry so that back
      // navigation works
      return { newFilters, applyFilters: () => apply(next, { replace: hasRejected }) }
    }),
  )
}

/**
 * Ensure that default filters (e.g. current category) are included in the options list. This is
 * needed since we do not currently `AND` the current category (i.e. default) filters with the
 * user-selected filters.
 */
const ensureOptionsIncludeDefault = (
  options: ProductAggregation['options'],
  defaultFilters?: ReadonlyArray<string> | null,
) => {
  if (!defaultFilters || defaultFilters.length <= 0) return options
  const { defaultOptions, remainingDefaultFilters, restOptions } = options.reduce(
    (
      acc: {
        defaultOptions: ProductAggregation['options']
        remainingDefaultFilters: string[]
        restOptions: ProductAggregation['options']
      },
      option,
    ) => {
      const remainingDefaultFilterIndex = acc.remainingDefaultFilters.indexOf(option.value)
      if (remainingDefaultFilterIndex >= 0) {
        acc.remainingDefaultFilters.splice(remainingDefaultFilterIndex, 1)
        acc.defaultOptions.push(option)
        return acc
      }
      acc.restOptions.push(option)
      return acc
    },
    { defaultOptions: [], remainingDefaultFilters: defaultFilters.slice(), restOptions: [] },
  )
  // render default filters (e.g. current category) at the top of the list
  return [
    ...defaultOptions,
    ...remainingDefaultFilters.map((value) => ({ count: 0, label: value, value })),
    ...restOptions,
  ]
}
const ExpandMoreOrLess = styled.button`
  display: inline-flex;
  width: 100%;
  border: none;
  padding: 0;
  background-color: transparent;
  justify-content: space-between;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`
const ViewMoreOrLess = styled.button`
  display: inline-flex;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  width: 100%;
  border: none;
  padding: 0;
  background-color: transparent;
  justify-content: unset;
  gap: 7px;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`

const Filter: FC<FilterProps> = ({ className, id, aggregation, listing }) => {
  const { label } = aggregation
  const { defaultState, state } = listing
  const defaultFilters = defaultState.filters[id]
  const options = useMemo(() => {
    const possiblySortedOptions =
      id === 'nameRange'
        ? sortOptionsByLabel(aggregation.options)
        : id === 'labelStatus'
          ? mapLabelStatusOptions(aggregation.options)
          : aggregation.options
    return ensureOptionsIncludeDefault(possiblySortedOptions, defaultFilters)
  }, [aggregation.options, defaultFilters, id])

  const stateValues = state.filters[id]

  const { getValues, register, watch, setValue } = useForm<FormFields>({
    defaultValues: defaultValuesForState(id, options, stateValues),
  })

  // Reset form values when state changes. This allows back/forwards navigation to work as expected.
  useEffect(() => {
    const defaultValues = defaultValuesForState(id, options, stateValues)
    // use setValue instead of reset to avoid `options[0]` undefined bug
    setValue(id, defaultValues[id])
  }, [id, options, setValue, stateValues])

  const formValues = watch(id)

  useEffect(() => {
    // push form input updates to keyed signal for observable pipeline
    setFormInput({ id, input: formValues })
  }, [formValues, id])

  useEffect(() => {
    // get current form values - skip in observable pipeline to avoid accidental application of
    // previous values
    const formInputToSkip = getValues(id)
    const subscription = applyFiltersOnFormInput$(id, options, listing, formInputToSkip).subscribe(
      ({ applyFilters }) => {
        applyFilters()
      },
    )
    return () => subscription.unsubscribe()
  }, [getValues, id, listing, options])

  useEffect(() => {
    const stateFilters = sortValues(stateValues)
    if (stateFilters.length === 0) {
      setValue(id, options.length === 1 ? false : [])
    }
  }, [id, options.length, setValue, stateValues])

  const [expanded, setExpanded] = useState(true)
  const [viewMore, setViewMore] = useState(false)
  const showViewMore = options.length > 5

  const checkboxOptions = useMemo(() => {
    const filteredOptions = parseFilterOptionsForCheckboxGroup({
      disabledValues: defaultState.filters[id] || [],
      options,
    })

    if (!viewMore) {
      return filteredOptions.slice(0, 5)
    }

    return filteredOptions
  }, [defaultState.filters, id, options, viewMore])

  return (
    <div className={className}>
      <ExpandMoreOrLess onClick={() => setExpanded(() => !expanded)}>
        <h5>{label?.toUpperCase()}</h5>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </ExpandMoreOrLess>
      {expanded && (
        <>
          <CheckboxGroup
            name={id}
            checkboxOptions={checkboxOptions}
            disabled={false}
            register={register}
          />
          {showViewMore && (
            <ViewMoreOrLess onClick={() => setViewMore(() => !viewMore)}>
              {viewMore ? (
                <>
                  View Less <ExpandLess />
                </>
              ) : (
                <>
                  View More <ExpandMore />
                </>
              )}
            </ViewMoreOrLess>
          )}
        </>
      )}
    </div>
  )
}

const StyledFilter = styled(Filter)(({ theme }) => ({
  padding: '0 10px',
  h5: {
    margin: '20px 0',
  },
  svg: {
    width: '16px',
    height: '16px',
  },
  // is this needed?
  fieldset: {
    padding: '0',
    margin: '0 0 20px 0',
    border: 'none',
  },
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.colors.coolGray.cool450}`,
    marginBottom: '20px',
  },
}))

export default StyledFilter
